import { memo, useCallback, useContext, useMemo, useState } from 'react'
import { notifyError } from '../../../../../utils/notifications'
import { useParams } from 'react-router-dom'
import { Button as AntdButton, Flex, Form, Modal, Typography } from 'antd'
import Button from '../../../../common/Button'
import InputText from '../../../../common/InputText'
import { postRequest } from '../../../../../services/base'
import { decrypt } from '../../../../../utils/encryption'
import { NavContext } from '../../../../../context/global_context'
import { useIntl } from 'react-intl'
import useDebouncedValue from '../../../../../utils/common_function'
import {
  useMappedContext,
  usePageContext,
  SET_FUNCTIONAL_DIAGRAM_DETAILS,
  RESET_SELECTED_MAPPED,
} from '../context/context'
import MappedConnectorsTable from './MappedConnectorsTable'
import useTableScrollY, { tableAreaStyle } from '../useTableScrollY'

const MappedConnectorsSection = () => {
  const intl = useIntl()
  const { functional, vehicle } = useParams()
  const [mappedState, dispatch] = useMappedContext()
  const [pageState] = usePageContext()
  const [, navDispatch] = useContext(NavContext)
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearch = useDebouncedValue(searchValue, 500)
  const [modal, contextHolder] = Modal.useModal()
  const [tableAreaRef, tableHeight] = useTableScrollY()

  const filteredMapped = useMemo(() => {
    if (!debouncedSearch) return mappedState.mappedConnectors
    return mappedState.mappedConnectors.filter((row) =>
      `${row.name} ${row.description}`
        .toLowerCase()
        .includes(debouncedSearch.toLowerCase())
    )
  }, [mappedState.mappedConnectors, debouncedSearch])

  const deleteMapping = useCallback(async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({
          id: 'functionalDiagram.deletingMapping',
        }),
        showLoader: true,
      },
      notification: {},
    })

    const formData = new FormData()
    formData.append('vehicle', vehicle)
    formData.append('action', 'delete')
    mappedState.selectedMappedIds.forEach((id) =>
      formData.append('connector', id)
    )

    let res = await postRequest(
      `/api/sub_system/functional-diagram/connector/${decrypt(functional)}/?vehicle=${vehicle}`,
      formData
    )

    // Falling through dispatched `undefined` for sales_code/data/category,
    // silently wiping the mapped connectors on screen.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'functionalDiagram.mappingUpdateError' }))
      return
    }
    dispatch({
      type: SET_FUNCTIONAL_DIAGRAM_DETAILS,
      data: {
        ...pageState.functionalDiagramDetails,
        sales_code_data: res.sales_code,
      },
      mappedConnector: res.data,
      connectorCategories: res.category,
    })

    navDispatch({
      type: 'notification',
      data: {
        notification_type: 'success',
        msg: intl.formatMessage({ id: 'functionalDiagram.mappingDeleteSuccess' }),
      },
    })
  }, [
    functional,
    vehicle,
    mappedState.selectedMappedIds,
    pageState.functionalDiagramDetails,
    dispatch,
    navDispatch,
    intl,
  ])

  const confirmDelete = useCallback(() => {
    modal.confirm({
      title: intl.formatMessage({ id: 'functionalDiagram.deleteConfirmTitle' }),
      content: intl.formatMessage({
        id: 'functionalDiagram.deleteConfirmContent',
      }),
      okText: intl.formatMessage({ id: 'forms.delete' }),
      okType: 'danger',
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      centered: true,
      maskClosable: true,
      onOk: deleteMapping,
    })
  }, [modal, deleteMapping, intl])

  const onClearMapped = useCallback(
    () => dispatch({ type: RESET_SELECTED_MAPPED }),
    [dispatch]
  )

  return (
    <div className="col-body" style={{ height: '100%' }}>
      {contextHolder}
      <Typography.Text strong>
        {intl.formatMessage({ id: 'functionalDiagram.mapped' })}
      </Typography.Text>
      <Form>
        <InputText
          type="search"
          name="search_mapped_connector"
          placeholder={intl.formatMessage({ id: 'forms.search' })}
          isRequired={false}
          value={searchValue}
          onChange={setSearchValue}
        />
      </Form>

      <div ref={tableAreaRef} style={tableAreaStyle} data-testid="fd-mapped">
        <MappedConnectorsTable rows={filteredMapped} scrollY={tableHeight} />
      </div>

      {/* {mappedState.selectedMappedIds?.length > 0 && ( */}
      {filteredMapped?.length > 0 && (
        <Flex gap={16} style={{ flex: '0 0 auto' }}>
          <AntdButton
            danger
            disabled={mappedState.selectedMappedIds?.length > 0 ? false : true}
            style={{ flex: 1 }}
            onClick={confirmDelete}
            data-testid="fd-delete-selected"
          >
            {intl.formatMessage({ id: 'functionalDiagram.deleteSelected' })}
          </AntdButton>
          <Button
            disabled={mappedState.selectedMappedIds?.length > 0 ? false : true}
            style={{ flex: 1 }}
            onClick={onClearMapped}
            testId="fd-clear-mapped"
          >
            {intl.formatMessage({ id: 'functionalDiagram.clearSelection' })}
          </Button>
        </Flex>
      )}
    </div>
  )
}

export default memo(MappedConnectorsSection)
